import { type ReactElement } from "react";
import {
  Compass,
  LifeBuoy,
  LogOut,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Avatar, AvatarFallback } from "../atoms/index.js";
import {
  Popover,
  PopoverContent,
  PopoverMenuItem,
  PopoverSeparator,
  PopoverTrigger,
} from "../molecules/index.js";

export interface TopBarUser {
  /** Stable identity; seeds the avatar tone so it matches the person's
   * avatar everywhere else (d36). Falls back to the name. */
  readonly id?: string;
  readonly name: string;
  readonly initials: string;
}

export interface ProfileMenuProps {
  /** Signed-in user — drives the avatar initials and the menu header. */
  readonly user?: TopBarUser | undefined;
  /**
   * Open the Admin console. Capability by prop-presence: the host passes this
   * only for users who may administer the tenant, so the item never renders
   * for anyone else; the server stays authoritative.
   */
  readonly onAdmin?: (() => void) | undefined;
  /**
   * Open personal Settings for every signed-in role. Administrators receive
   * this alongside `onAdmin`; other roles receive Settings on its own.
   */
  readonly onTeams?: (() => void) | undefined;
  readonly onSettings?: (() => void) | undefined;
  readonly onPlatformHelp?: (() => void) | undefined;
  /**
   * Replay the onboarding tour for whatever the user is currently looking at.
   *
   * Capability by prop-presence like the rest: the host passes it only when
   * this page actually has a tour, so the item never offers to replay nothing.
   * Beside Platform help because that is what it is — a way back to the
   * explanation, not a setting.
   */
  readonly onReplayTour?: (() => void) | undefined;
  readonly onLogout?: (() => void) | undefined;
}

/**
 * The avatar trigger keeps a circular hover paint, while the
 * utility buttons beside it are 32px squares (`ChromeIconButton` `bar`): a
 * square hover wash under a round avatar reads as a misregistration.
 */
const AVATAR_TRIGGER_CLASS =
  "group ms-1 inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:pb-overlay-paint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal focus-visible:ring-offset-0";

/**
 * The top bar's profile popover (PB-5176 split it out of `TopBar` so both
 * files stay under the 200-line soft cap). Identity header, then the role-aware
 * Admin console, Settings, Platform help and Logout — each only when
 * the host supplies its handler, so no item ever closes the menu without doing
 * anything. The theme control is no longer here:
 * decision d9 moved it into the bar as host content (`themeToggleSlot`) so the
 * control has one home.
 */
export function ProfileMenu({
  user,
  onAdmin,
  onSettings,
  onTeams,
  onPlatformHelp,
  onReplayTour,
  onLogout,
}: ProfileMenuProps): ReactElement {
  const initials = user?.initials ?? "·";
  const name = user?.name ?? "Signed in";
  // The person's own gradient (Avatar seed, OK-65): the same seed the
  // roster and every other avatar use.
  const seed = user?.id ?? user?.name ?? initials;

  return (
    <Popover align="end">
      <PopoverTrigger>
        <button
          type="button"
          aria-label="Profile"
          className={AVATAR_TRIGGER_CLASS}
        >
          {/* Avatar has no 28px size (xs is 24px, sm 32px), so the trigger
              keeps today's 28px disc with this one size override. The
              missing step is reported, like OwnerChip's 20px disc (OK-65). */}
          <Avatar seed={seed} className="h-7 w-7">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex items-center gap-2.5 px-2.5 py-2">
          <Avatar size="sm" seed={seed} className="shrink-0">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium leading-tight text-pb-text">
              {name}
            </p>
          </div>
        </div>

        <PopoverSeparator />

        {onTeams ? (
          <PopoverMenuItem icon={Users} onClick={() => onTeams()}>
            Teams
          </PopoverMenuItem>
        ) : null}
        {onAdmin ? (
          <PopoverMenuItem icon={ShieldCheck} onClick={() => onAdmin()}>
            Admin console
          </PopoverMenuItem>
        ) : null}
        {onSettings ? (
          <PopoverMenuItem icon={Settings} onClick={() => onSettings()}>
            Settings
          </PopoverMenuItem>
        ) : null}
        {onReplayTour ? (
          <PopoverMenuItem icon={Compass} onClick={() => onReplayTour()}>
            Replay tour
          </PopoverMenuItem>
        ) : null}
        {onPlatformHelp ? (
          <PopoverMenuItem icon={LifeBuoy} onClick={() => onPlatformHelp()}>
            Platform help
          </PopoverMenuItem>
        ) : null}
        {onLogout ? (
          <>
            <PopoverSeparator />
            <PopoverMenuItem icon={LogOut} onClick={() => onLogout()}>
              Logout
            </PopoverMenuItem>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
